// FILE: client/src/screens/admin/AdminCategoryListScreen.jsx

import React, { useEffect, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Badge, Button, Col, Form, Image, Row, Table } from "react-bootstrap";

import Loader from "../../components/Loader";
import Message from "../../components/Message";
import {
  CATALOGS,
  JEWELRY_HIRE_CATALOG_KEY,
  isValidCatalogKey,
} from "../../constants/shopMeta";
import {
  adminDeleteCategory,
  adminListCategories,
  resetAdminCategoryDelete,
} from "../../actions/categoryActions";

const DEFAULT_SHOP_KEY = CATALOGS?.[0]?.shopKey || "haberdashery";

const getCategoryId = (category) => category?._id || category?.id || "";

const getCoverImageUrl = (category) => {
  const firstImage = Array.isArray(category?.images)
    ? category.images[0]
    : null;

  if (!firstImage) return "";

  if (typeof firstImage === "string") return firstImage;

  return firstImage.url || "";
};

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString();
};

const AdminCategoryListScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { shopKey: routeShopKey } = useParams();

  const selectedShopKey = useMemo(() => {
    const safeRouteShopKey = String(routeShopKey || "").trim();

    return isValidCatalogKey(safeRouteShopKey)
      ? safeRouteShopKey
      : DEFAULT_SHOP_KEY;
  }, [routeShopKey]);

  const adminCategoryListState = useSelector(
    (state) => state.adminCategoryList || {},
  );

  const {
    loading,
    error,
    categories: rawCategories = [],
  } = adminCategoryListState;

  const categories = Array.isArray(rawCategories) ? rawCategories : [];
  const isJewelryCatalogue =
    selectedShopKey === JEWELRY_HIRE_CATALOG_KEY;

  const adminCategoryDeleteState = useSelector(
    (state) => state.adminCategoryDelete || {},
  );

  const {
    loading: loadingDelete,
    error: errorDelete,
    success: successDelete,
  } = adminCategoryDeleteState;

  useEffect(() => {
    if (!isValidCatalogKey(routeShopKey)) {
      navigate(`/admin/shop/${DEFAULT_SHOP_KEY}/categories`, {
        replace: true,
      });
    }
  }, [navigate, routeShopKey]);

  useEffect(() => {
    if (!isValidCatalogKey(selectedShopKey)) return;

    dispatch(
      adminListCategories({
        shopKey: selectedShopKey,
        pageNumber: 1,
        pageSize: 200,
      }),
    );
  }, [dispatch, selectedShopKey]);

  useEffect(() => {
    if (!successDelete) return;

    dispatch(resetAdminCategoryDelete());

    dispatch(
      adminListCategories({
        shopKey: selectedShopKey,
        pageNumber: 1,
        pageSize: 200,
      }),
    );
  }, [dispatch, selectedShopKey, successDelete]);

  const handleShopChange = (event) => {
    const nextShopKey = event.target.value;

    if (!isValidCatalogKey(nextShopKey)) return;

    navigate(`/admin/shop/${nextShopKey}/categories`);
  };

  const deleteHandler = async (category) => {
    const categoryId = getCategoryId(category);

    if (!categoryId) {
      window.alert("Cannot delete this category because its id is missing.");
      return;
    }

    const confirmed = window.confirm(
      `Delete category "${category?.name || "Untitled"}"?`,
    );

    if (!confirmed) return;

    dispatch(adminDeleteCategory(categoryId));
  };

  return (
    <>
      <Row className="align-items-end mb-3 gy-3">
        <Col md>
          <h1 className="mb-1">Categories</h1>
          <div className="text-muted">Manage categories by catalogue.</div>
        </Col>

        <Col md={4} lg={3}>
          <Form.Group controlId="shopSelector">
            <Form.Label className="fw-semibold">Catalogue</Form.Label>

            <Form.Select
              value={selectedShopKey}
              onChange={handleShopChange}
              disabled={loading || loadingDelete}
            >
              {CATALOGS.map((shop) => (
                <option key={shop.shopKey} value={shop.shopKey}>
                  {shop.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col xs="auto">
          <Button
            as={Link}
            to={`/admin/shop/${selectedShopKey}/categories/create`}
            disabled={!isValidCatalogKey(selectedShopKey)}
          >
            Create Category
          </Button>
        </Col>
      </Row>

      {loadingDelete ? <Loader /> : null}

      {errorDelete ? <Message variant="danger">{errorDelete}</Message> : null}

      {loading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error}</Message>
      ) : categories.length === 0 ? (
        <Message>No categories found for this shop.</Message>
      ) : (
        <Table striped bordered hover responsive className="align-middle">
          <thead>
            <tr>
              <th style={{ width: 72 }}>IMAGE</th>
              <th>NAME</th>
              {!isJewelryCatalogue ? <th>SLUG</th> : null}
              {isJewelryCatalogue ? <th>ORDER</th> : null}
              <th>PUBLISHED</th>
              {!isJewelryCatalogue ? <th>CREATED</th> : null}
              <th className="text-end">ACTIONS</th>
            </tr>
          </thead>

          <tbody>
            {categories.map((category) => {
              const categoryId = getCategoryId(category);
              const coverImageUrl = getCoverImageUrl(category);

              return (
                <tr key={categoryId}>
                  <td>
                    {coverImageUrl ? (
                      <Image
                        src={coverImageUrl}
                        alt={category.name || "Category image"}
                        rounded
                        style={{
                          width: 48,
                          height: 48,
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>

                  <td>{category.name}</td>

                  {!isJewelryCatalogue ? (
                    <td>
                      <code>{category.slug}</code>
                    </td>
                  ) : null}

                  {isJewelryCatalogue ? (
                    <td>{category.displayOrder ?? 0}</td>
                  ) : null}

                  <td>
                    {category.isPublished ? (
                      <Badge bg="success">Published</Badge>
                    ) : (
                      <Badge bg="secondary">Draft</Badge>
                    )}
                  </td>

                  {!isJewelryCatalogue ? (
                    <td>{formatDate(category.createdAt)}</td>
                  ) : null}

                  <td className="text-end text-nowrap">
                    <Button
                      as={Link}
                      to={`/admin/shop/${selectedShopKey}/categories/${categoryId}/edit`}
                      variant="light"
                      size="sm"
                      className="me-2"
                    >
                      Edit
                    </Button>

                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => deleteHandler(category)}
                      disabled={loadingDelete}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </>
  );
};

export default AdminCategoryListScreen;
